import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import { message } from "antd";
import { login } from "../apicalls/loginAPICall";
import useAuth from "../customHooks/useAuth";
import { jwtDecode } from "jwt-decode";
import { calculateSessionExpiry } from "../config/sessionConfig";
import Cookies from "js-cookie";
//import rsbgLogo from "../data/rsbgloballogo.jpg";
import smartweldPNGLogo from "../data/smartweldLogo.png"

const Login = () => {
  const { userLogin } = useAuth();
  const [emailAddress, setEmailAddress] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);

  const navigate = useNavigate();
  const reference = useRef();

  useEffect(() => {
    // Check if user is already authenticated by checking cookies and session directly
    const checkAuthentication = () => {
      const token = Cookies.get("_smartweld_Cookie");
      const expireTime = sessionStorage.getItem("sessionExpire");

      console.log("Checking authentication:", { hasToken: !!token, hasExpireTime: !!expireTime });

      // If no token, user is not authenticated
      if (!token) {
        console.log("No token found");
        return false;
      }

      // If no expiration time, session is invalid
      if (!expireTime) {
        console.log("No expiration time found");
        return false;
      }

      // Check if token is expired
      try {
        const decodedToken = jwtDecode(token);
        const now = Date.now() / 1000; // JWT exp is in seconds

        if (decodedToken.exp && decodedToken.exp < now) {
          console.log("JWT token expired");
          // Token expired, clean up
          Cookies.remove("_smartweld_Cookie", { path: "/" });
          Cookies.remove("_smartweld_Cookie");
          sessionStorage.removeItem("_smartWeldUser");
          sessionStorage.removeItem("sessionExpire");
          return false;
        }
      } catch (error) {
        console.log("Error decoding token:", error);
        // Invalid token, clean up
        Cookies.remove("_smartweld_Cookie", { path: "/" });
        Cookies.remove("_smartweld_Cookie");
        sessionStorage.removeItem("_smartWeldUser");
        sessionStorage.removeItem("sessionExpire");
        return false;
      }

      // Check session expiration timestamp
      const expirationTimestamp = Number(expireTime);
      const now = Date.now();

      if (isNaN(expirationTimestamp) || expirationTimestamp <= 0 || now >= expirationTimestamp) {
        console.log("Session expired");
        // Session expired, clean up
        Cookies.remove("_smartweld_Cookie", { path: "/" });
        Cookies.remove("_smartweld_Cookie");
        sessionStorage.removeItem("_smartWeldUser");
        sessionStorage.removeItem("sessionExpire");
        return false;
      }

      // User is authenticated with valid session
      console.log("User is authenticated, redirecting...");
      return true;
    };

    // If user is already authenticated, redirect to dashboard immediately
    if (checkAuthentication()) {
      window.location.replace("/dashboard");
      return;
    }

    setCheckingAuth(false);
    // Focus email input
    reference.current?.focus();
  }, [navigate]);

  // Don't render login form while checking authentication
  if (checkingAuth) {
    return null; // or a loading spinner
  }

  const submitLoginHandler = async (e) => {
    e.preventDefault();

    // Prevent multiple submissions
    if (isLoading) return;

    setIsLoading(true);

    try {
      console.log("Email Address", emailAddress, "Password", password);
      const credentials = `${emailAddress}:${password}`;
      const encodeCredentials = btoa(credentials);

      const response = await login(encodeCredentials);

      // Handle timeout errors
      if (response.error === 'timeout') {
        message.error(response.message, 5);
        setIsLoading(false);
        return;
      }

      // Handle network errors
      if (response.error === 'network') {
        message.error(response.message, 5);
        setIsLoading(false);
        return;
      }

      // Handle successful login
      if (response.success === true) {
        message.success("Login Successful");
        const accessToken = response?.token;
        const decodedToken = jwtDecode(accessToken);

        // Calculate expiration timestamp using centralized config
        const expirationTimestamp = calculateSessionExpiry();

        const cookieData = {
          user_first_name: decodedToken?.user_first_name,
          user_id: decodedToken?.id,
          user_last_name: decodedToken?.user_last_name,
          orgId: decodedToken?.org_id,
          locationId: decodedToken?.location_id,
          siteId: decodedToken?.site_id,
          lineTypeId: decodedToken?.line_type_id,
          roleId: decodedToken?.role_id,
          roleName: decodedToken?.role_name,
          email: decodedToken?.user_email,
        };

        // Pass the expiration timestamp (number) to userLogin
        const loginSuccess = userLogin(
          accessToken,
          cookieData,
          expirationTimestamp
        );

        if (loginSuccess) {
          console.log("Session will expire at:", new Date(expirationTimestamp).toLocaleString());

          // Clear form
          setEmailAddress("");
          setPassword("");
          setIsLoading(false);

          // Navigate with replace to prevent back button issues
          navigate("/dashboard", { replace: true });
        } else {
          message.error("Failed to establish session");
          setIsLoading(false);
        }
      } else {
        // Handle login failure
        console.log("Response error", response);
        message.error(
          response?.message ||
          response?.response?.data?.message ||
          "Login failed"
        );
        setIsLoading(false);
      }
    } catch (error) {
      console.log("Login error", error);
      message.error(error.message || "An unexpected error occurred");
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Login Form */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 bg-white">
        <div className="w-full max-w-md space-y-8">
          {/* Logo/Brand */}
          <div className="text-center">
            <div className="flex justify-center mb-4">
              <div className="w-full h-16 bg-gradient-to-tr from-blue-900 to-gray-900 rounded-2xl flex items-center justify-center shadow-lg">
                <span className="material-symbols-outlined text-4xl text-white">
                  {/* <img src={rsbgLogo} alt="Smartweld" className="w-full h-10 rounded-[7px] object-contain"  /> */}
                  <img src={smartweldPNGLogo} alt="Smartweld" className="w-full h-16 p-1 rounded-[7px] object-contain"  />
                </span>
              </div>
            </div>
            <h2 className="text-3xl font-bold text-gray-900">Smartweld</h2>
            <p className="mt-2 text-sm text-gray-600">
              Sign in to your account
            </p>
          </div>

          {/* Login Form */}
          <form onSubmit={submitLoginHandler} className="mt-8 space-y-6">
            <div className="space-y-4">
              {/* Email Input */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email address
                </label>
                <input
                  ref={reference}
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={emailAddress}
                  onChange={(e) => setEmailAddress(e.target.value)}
                  disabled={isLoading}
                  className="appearance-none relative block w-full px-3 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed transition-all"
                  placeholder="Enter your email"
                />
              </div>

              {/* Password Input */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                  Password
                </label>
                <div className="relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={isLoading}
                    className="appearance-none relative block w-full px-3 py-3 pr-10 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed transition-all"
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none disabled:opacity-50"
                    disabled={isLoading}
                    tabIndex={-1}
                  >
                    <span className="material-symbols-outlined text-xl">
                      {showPassword ? "visibility_off" : "visibility"}
                    </span>
                  </button>
                </div>
              </div>
            </div>

            {/* Forgot Password Link */}
            <div className="flex items-center justify-end">
              <Link
                to="/forgotPassword"
                className="text-sm font-medium text-gray-900 hover:text-gray-700 transition-colors"
              >
                Forgot password?
              </Link>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-semibold rounded-lg text-white bg-gradient-to-r from-gray-900 to-gray-800 hover:from-gray-800 hover:to-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-md hover:shadow-lg"
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Signing in...
                </span>
              ) : (
                "Sign in"
              )}
            </button>
          </form>
        </div>
      </div>





      {/* Right Side - Testimonial/Branding */}
      <div className="hidden lg:flex lg:flex-1 bg-gradient-to-br from-black via-blue-950 to-gray-900 relative overflow-hidden">
        {/* Meteor Effects */}
        {[...Array(8)].map((_, idx) => (
          <span
            key={idx}
            className="absolute animate-meteor-effect"
            style={{
              top: `${Math.random() * -20}%`,
              left: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${3 + Math.random() * 3}s`,
            }}
          >
            <div className="h-[1px] w-[50px] bg-gradient-to-r from-transparent via-blue-400 to-transparent opacity-70 shadow-[0_0_10px_2px_rgba(59,130,246,0.5)]"
              style={{
                transform: 'rotate(60deg)',
              }}
            />
          </span>
        ))}

        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, rgba(100,116,139,0.3) 1px, transparent 0)`,
            backgroundSize: '40px 40px'
          }}></div>
        </div>

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-start px-12 py-8 text-white overflow-y-auto">
          <div className="max-w-md">
            {/* Quote Icon */}
            <div className="mb-6">
              <svg className="w-12 h-12 text-blue-300 opacity-50" fill="currentColor" viewBox="0 0 32 32">
                <path d="M9.352 4C4.456 7.456 1 13.12 1 19.36c0 5.088 3.072 8.064 6.624 8.064 3.36 0 5.856-2.688 5.856-5.856 0-3.168-2.208-5.472-5.088-5.472-.576 0-1.344.096-1.536.192.48-3.264 3.552-7.104 6.624-9.024L9.352 4zm16.512 0c-4.8 3.456-8.256 9.12-8.256 15.36 0 5.088 3.072 8.064 6.624 8.064 3.264 0 5.856-2.688 5.856-5.856 0-3.168-2.304-5.472-5.184-5.472-.576 0-1.248.096-1.44.192.48-3.264 3.456-7.104 6.528-9.024L25.864 4z" />
              </svg>
            </div>

            {/* Testimonial */}
            <blockquote className="space-y-4">
              <p className="text-xl font-medium leading-relaxed">
                "Smart Weld has revolutionized our welding operations. The real-time monitoring and data analytics have significantly improved our productivity and quality control."
              </p>
              <footer className="flex items-center space-x-3 mt-6">
                <div className="flex -space-x-2">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 border-2 border-white flex items-center justify-center text-sm font-semibold">
                    JD
                  </div>
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-400 to-purple-600 border-2 border-white flex items-center justify-center text-sm font-semibold">
                    SM
                  </div>
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-400 to-green-600 border-2 border-white flex items-center justify-center text-sm font-semibold">
                    AK
                  </div>
                </div>
                <div className="ml-3">
                  <div className="text-sm font-semibold">Manufacturing Team</div>
                  <div className="text-xs text-blue-200">Industry Leaders</div>
                </div>
              </footer>
            </blockquote>

            {/* Stats */}
            <div className="mt-8 grid grid-cols-3 gap-6 pt-6 border-t border-white/10">
              <div>
                <div className="text-3xl font-bold">500+</div>
                <div className="text-sm text-blue-200 mt-1">Active Users</div>
              </div>
              <div>
                <div className="text-3xl font-bold">50K+</div>
                <div className="text-sm text-blue-200 mt-1">Welds Tracked</div>
              </div>
              <div>
                <div className="text-3xl font-bold">99.9%</div>
                <div className="text-sm text-blue-200 mt-1">Uptime</div>
              </div>
            </div>
          </div>
        </div>

        {/* Decorative Elements - Darker glow effects */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl"></div>
      </div>

      {/* custom CSS for meteor animation */}
      <style jsx>{`
        @keyframes meteor-effect {
          0% {
            opacity: 0;
            transform: translateY(0) translateX(0);
          }
          5% {
            opacity: 1;
          }
          95% {
            opacity: 1;
          }
          100% {
            opacity: 0;
            transform: translateY(300px) translateX(-173px); /* 60 degree: tan(60°) ≈ 1.732 */
          }
        }

        .animate-meteor-effect {
          animation: meteor-effect linear infinite;
          will-change: transform, opacity;
        }
      `}</style>
    </div>
  );
};

export default Login;